// Progression and kill rewards. One XP curve for trainers and creatures; XP and
// coins per kill depend only on the wild's level (the hunt level).
import { CONFIG } from './config.ts';

/** Total XP needed to reach a level from level 1. Cubic, so each level costs ~50 × level². */
export function totalXp(level: number): number {
  if (level <= 1) return 0;
  const L = level;
  return Math.round((50 / 3) * (L * L * L - 6 * L * L + 17 * L - 12));
}

/** XP needed to go from `level` to `level + 1`. Trainer and creature XP are stored within the current level. */
export function xpToNext(level: number): number {
  return totalXp(level + 1) - totalXp(level);
}
export const trainerXpToNext = xpToNext;
export const creatureXpToNext = xpToNext;

/**
 * XP a kill pays to the trainer and the fighting creature: CONFIG.killXpMult × (0.6 n² + 36) up to level 150, then grows
 * with n^1.1. The constant mostly lifts hunts under level 40, where players farm a little below their level.
 */
export function killXp(wildLevel: number): number {
  const n = Math.max(1, wildLevel);
  const base = n <= 150 ? Math.floor(0.6 * n * n) + 36 : 13_500 * Math.pow(n / 150, 1.1);
  return Math.max(1, Math.round(base * CONFIG.killXpMult));
}

const COIN_EXP = Math.log(6000 / 36) / Math.log(5000);
export const KILL_COINS_CAP = 15_776;

/** Coins a kill pays: 36 at level 1, 6,000 at level 5,000, never more than 15,776. */
export function killCoins(wildLevel: number): number {
  return Math.min(KILL_COINS_CAP, Math.max(1, Math.round(36 * Math.pow(Math.max(1, wildLevel), COIN_EXP))));
}

/** Coins a trainer level-up pays: CONFIG.levelUpCoinKills kills' worth at a hunt of the new level. */
export function levelUpCoins(level: number): number {
  return CONFIG.levelUpCoinKills * killCoins(level);
}

/**
 * Every kill pays this share on top of killCoins: what the old junk loot (Frosty Pelt, Pottery Shard,
 * Torn Lantern) was worth on average when sold. Junk was removed on 2026-10-16, as the reference did
 * in its v1.298, and its value folded into kill coins.
 */
export const LOOT_SHARE = 0.15;

/** What a hunt kill pays before perks and boosts: killCoins plus LOOT_SHARE (rounded, so 6,000 pays 6,900). */
export function huntKillCoins(wildLevel: number): number {
  return Math.round(killCoins(wildLevel) * (1 + LOOT_SHARE));
}

/** Share of in-level trainer XP lost when the whole team is knocked out. Never costs a level. */
export const WIPE_XP_LOSS = 0.1;

/** A non-starter creature can fight only up to this many levels above its trainer. */
export const LEVEL_SLACK = 5;

/** Whether a creature may lead or swap in for a trainer of this level. The starter is always allowed. */
export function canFight(c: { level: number; ground: string }, trainerLevel: number): boolean {
  return c.ground === 'starter' || c.level <= trainerLevel + LEVEL_SLACK;
}
